import { useState } from "react";
import {
  Alert,
  alpha,
  Box,
  Checkbox,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { settingValueMembers } from "@thalamos/haven-common";
import type {
  HolderConfiguration,
  SettingChange,
  SettingHolder,
  SettingValueJson,
  SettingView,
} from "@thalamos/haven-common";

import {
  describeChoice,
  describeSettingValue,
  GROUP_OF_SETTING,
  SETTING_GROUPS,
  SETTING_UNIT,
} from "./settingPresentation.js";
import { shownSetting, type UnsavedSettings } from "./unsavedSettings.js";

const describeSource = (source: SettingHolder, fallback: string): string =>
  source.kind === "default" ? fallback : source.name;

type ControlProps = {
  setting: SettingView;
  /**
   * What the control shows: what applies, stated or inherited, with any
   * unsaved change — so a disabled control is a true picture rather than an
   * empty one.
   */
  value: SettingValueJson;
  label: string;
  disabled: boolean;
  onChange: (value: SettingValueJson) => void;
};

/**
 * A number is changed once it is finished with, on Enter or on leaving the field,
 * rather than per keystroke: typing 365 would otherwise change it to 3 and then 36 on
 * the way.
 */
const WholeNumberControl = ({
  setting,
  value,
  label,
  disabled,
  onChange,
}: ControlProps) => {
  const { t } = useTranslation();
  const current = String(value);
  const [typed, setTyped] = useState(current);
  const unit = SETTING_UNIT[setting.key];

  const commit = () => {
    const proposed = Number(typed);

    const outOfRange =
      setting.range !== undefined &&
      (proposed < setting.range.minimum || proposed > setting.range.maximum);

    // `Number("")` is 0, so an emptied field would otherwise become zero.
    // Put back rather than kept, because a value the server would refuse would
    // otherwise stay in the field looking as though it would save.
    if (typed.trim() === "" || !Number.isInteger(proposed) || outOfRange) {
      setTyped(current);
      return;
    }

    if (String(proposed) !== current) {
      onChange(proposed);
    }
  };

  return (
    <TextField
      type="number"
      size="small"
      value={typed}
      disabled={disabled}
      sx={{ maxWidth: 200 }}
      slotProps={{
        htmlInput: {
          "aria-label": label,
          min: setting.range?.minimum,
          max: setting.range?.maximum,
          step: 1,
        },
        input:
          unit === null
            ? {}
            : {
                endAdornment: (
                  <InputAdornment position="end">{t(unit)}</InputAdornment>
                ),
              },
      }}
      onChange={(event) => {
        setTyped(event.target.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
        }
      }}
    />
  );
};

const SettingControl = (props: ControlProps) => {
  const { t } = useTranslation();
  const { setting, value, label, disabled, onChange } = props;

  // The value is read through the shape its own declaration says it has, rather
  // than tested for at each use: `settingValueMembers` gives a set its members
  // and `String` gives a single choice its own text, so there is no "what if it
  // is the other shape" branch here to get wrong or to leave untested.
  if (setting.valueKind === "boolean") {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Switch
          checked={value === true}
          disabled={disabled}
          slotProps={{ input: { "aria-label": label } }}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <Typography variant="body2" aria-hidden>
          {describeSettingValue({ t, key: setting.key, value })}
        </Typography>
      </Stack>
    );
  }

  if (setting.valueKind === "whole_number") {
    // Keyed by what it shows, so a value put back by discarding replaces
    // whatever was typed.
    return <WholeNumberControl key={String(value)} {...props} />;
  }

  if (setting.valueKind === "choice") {
    return (
      <Select
        size="small"
        value={String(value)}
        disabled={disabled}
        sx={{ minWidth: 200 }}
        inputProps={{ "aria-label": label }}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {setting.choices.map((choice) => (
          <MenuItem key={choice} value={choice}>
            {describeChoice({ t, key: setting.key, choice })}
          </MenuItem>
        ))}
      </Select>
    );
  }

  const selected = settingValueMembers(value);

  return (
    <Select
      multiple
      size="small"
      value={selected}
      disabled={disabled}
      fullWidth
      inputProps={{ "aria-label": label }}
      renderValue={(chosen) =>
        describeSettingValue({ t, key: setting.key, value: chosen })
      }
      // MUI reports a multiple selection as an array, and as a single string only
      // where a browser autofills one. Flattening covers both without asking
      // which happened.
      onChange={(event) => {
        onChange([event.target.value].flat());
      }}
    >
      {setting.choices.map((choice) => (
        <MenuItem key={choice} value={choice}>
          <Checkbox checked={selected.includes(choice)} />
          <ListItemText
            primary={describeChoice({ t, key: setting.key, choice })}
          />
        </MenuItem>
      ))}
    </Select>
  );
};

type SettingRowProps = {
  setting: SettingView;
  holder: SettingHolder;
  change: SettingChange | undefined;
  disabled: boolean;
  onChangeSetting: (setting: SettingView, change: SettingChange) => void;
};

const SettingRow = ({
  setting,
  holder,
  change,
  disabled,
  onChangeSetting,
}: SettingRowProps) => {
  const { t } = useTranslation();
  const shown = shownSetting(setting, change);
  const name = t(`admin.configuration.settings.keys.${setting.key}`);
  const sourceDefault = t("admin.configuration.settings.sourceDefault");
  const describe = (value: SettingValueJson) =>
    describeSettingValue({ t, key: setting.key, value });

  return (
    <TableRow
      sx={
        change === undefined
          ? undefined
          : {
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              boxShadow: (theme) =>
                `inset 4px 0 0 ${theme.palette.warning.main}`,
            }
      }
    >
      <TableCell sx={{ width: "30%" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(`admin.configuration.settings.descriptions.${setting.key}`)}
        </Typography>
      </TableCell>

      <TableCell>
        {/*
          No visible label beside it: the column header already says what the
          switch is, and a wrapping `<label>` would compete with the accessible
          name that tells the rows' switches apart.
        */}
        <Switch
          checked={shown.stated}
          disabled={disabled}
          slotProps={{
            input: {
              "aria-label": t("admin.configuration.settings.overrideFor", {
                setting: name,
              }),
            },
          }}
          // Turning the switch on states what the body is already getting, so the
          // value does not jump at the moment somebody takes control of it.
          // Turning it off clears the row, which is different from storing
          // today's inherited value: the body goes back to following whatever the
          // level above says next.
          onChange={(event) => {
            onChangeSetting(
              setting,
              event.target.checked
                ? { kind: "set", key: setting.key, value: shown.value }
                : { kind: "clear", key: setting.key },
            );
          }}
        />
      </TableCell>

      <TableCell sx={{ width: "35%" }}>
        <SettingControl
          setting={setting}
          value={shown.value}
          label={name}
          disabled={disabled || !shown.stated}
          onChange={(value) => {
            onChangeSetting(setting, { kind: "set", key: setting.key, value });
          }}
        />
      </TableCell>

      <TableCell>
        {change === undefined ? (
          <>
            <Typography
              variant="body2"
              sx={{ fontWeight: shown.stated ? 600 : undefined }}
            >
              {describeSource(setting.source, sourceDefault)}
            </Typography>
            {!shown.stated && (
              <Typography variant="caption" color="text.secondary">
                {t("admin.configuration.settings.inherited", {
                  value: describe(setting.inherited),
                })}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ fontWeight: shown.stated ? 600 : undefined }}
            >
              {shown.stated
                ? describeSource(holder, sourceDefault)
                : t("admin.configuration.settings.inherited", {
                    value: describe(setting.inherited),
                  })}
            </Typography>
            <Typography
              variant="caption"
              component="p"
              sx={{ color: "warning.dark", fontWeight: 600 }}
            >
              {setting.stated === null
                ? t("admin.configuration.settings.unsavedWasInherited", {
                    value: describe(setting.value),
                  })
                : t("admin.configuration.settings.unsavedWas", {
                    value: describe(setting.stated),
                  })}
            </Typography>
          </>
        )}
        {setting.clampedFrom !== undefined && (
          <Typography variant="caption" color="warning.main" component="p">
            {t("admin.configuration.settings.clamped", {
              asked: describe(setting.clampedFrom),
            })}
          </Typography>
        )}
        {setting.reconciledFrom !== undefined && (
          <Typography variant="caption" color="warning.main" component="p">
            {t("admin.configuration.settings.reconciled", {
              asked: describe(setting.reconciledFrom),
            })}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

export type SettingsEditorProps = {
  configuration: HolderConfiguration;
  unsaved: UnsavedSettings;
  saving: boolean;
  error: string | null;
  onChangeSetting: (setting: SettingView, change: SettingChange) => void;
  /** Whether to name the configurations the holder takes its settings from. */
  showChain?: boolean;
};

/**
 * Renders one holder's settings from the declarations the server sent with them,
 * grouped under headings and described in words rather than stored codes, with
 * the unsaved changes marked beside what they replace.
 *
 * There is no control per setting here, deliberately: each row is drawn from its
 * own declaration, and the presentation tables in `settingPresentation.ts` are
 * keyed by `SettingKey`, so the compiler names what a new setting is missing
 * there.
 *
 * Nothing here folds a chain either. `value`, `source` and `inherited` all arrive
 * resolved, so the page cannot disagree with the server about what applies.
 */
export const SettingsEditor = ({
  configuration,
  unsaved,
  saving,
  error,
  onChangeSetting,
  showChain = false,
}: SettingsEditorProps) => {
  const { t } = useTranslation();
  const sourceDefault = t("admin.configuration.settings.sourceDefault");

  return (
    <Stack spacing={1}>
      <Typography variant="h6" component="h2">
        {t("admin.configuration.settings.title")}
      </Typography>

      {showChain && configuration.chain.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("admin.configuration.settings.chain", {
            chain: configuration.chain
              .map((holder) => describeSource(holder, sourceDefault))
              .join(" → "),
          })}
        </Typography>
      )}

      {error !== null && <Alert severity="error">{error}</Alert>}

      <TableContainer
        component={Box}
        sx={{ border: 1, borderColor: "divider" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.configuration.settings.setting")}</TableCell>
              <TableCell>
                {t("admin.configuration.settings.override")}
              </TableCell>
              <TableCell>{t("admin.configuration.settings.applies")}</TableCell>
              <TableCell>{t("admin.configuration.settings.source")}</TableCell>
            </TableRow>
          </TableHead>
          {SETTING_GROUPS.map((group) => {
            const settings = configuration.settings.filter(
              (setting) => GROUP_OF_SETTING[setting.key] === group,
            );

            return settings.length === 0 ? null : (
              <TableBody key={group}>
                <TableRow>
                  <TableCell
                    colSpan={4}
                    component="th"
                    scope="rowgroup"
                    sx={{ bgcolor: "action.hover", fontWeight: 600 }}
                  >
                    {t(`admin.configuration.settings.groups.${group}`)}
                  </TableCell>
                </TableRow>
                {settings.map((setting) => (
                  <SettingRow
                    key={setting.key}
                    setting={setting}
                    holder={configuration.holder}
                    change={unsaved.get(setting.key)}
                    disabled={saving}
                    onChangeSetting={onChangeSetting}
                  />
                ))}
              </TableBody>
            );
          })}
        </Table>
      </TableContainer>
    </Stack>
  );
};
